import { Subject, forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, debounceTime, switchMap, take, takeUntil, map } from 'rxjs/operators';
import {
  Component,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatFormFieldAppearance, SubscriptSizing } from '@angular/material/form-field';
import { UnRx } from '@pebula/utils';
import { AsyncTracker, findPropertiesRegex, parseTemplatedKeysToString } from '@sg/utils';
import {
  SgAutoCompleteSelectQueryChange,
  SgAutoCompleteSelectComponent,
  SgAutoCompleteSelectGroupBy,
} from '@sg/ui-kit/auto-complete-select';
import {
  SgEntitySelectFetchStore,
  SgEntitySelectEntityFetchMap,
  SgEntityFetchAdapter,
  SgEntitySelectSearchResponse,
} from './entity-select-fetch-store.service';

const CANCEL_TOKEN = {};
const OPTIONS_RAW_OBJECT = Symbol('RAW_OBJECT');

export interface SgEntitySelectAutoCompleteGroupBy<T = any> {
  (option: T): string;
  labels: Record<string, string>;
}

@Component({
  selector: 'sg-entity-select-auto-complete',
  exportAs: 'sgEntityAutoCompleteSelect',
  templateUrl: './entity-select-auto-complete.component.html',
  styleUrls: ['./entity-select-auto-complete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@UnRx()
export class SgEntitySelectAutoCompleteComponent implements OnDestroy, AfterViewInit {
  @Input() panelClass: string;
  @Input() get entityKey(): keyof SgEntitySelectEntityFetchMap {
    return this._entityKey;
  }
  set entityKey(value: keyof SgEntitySelectEntityFetchMap) {
    if (value !== this.entityKey) {
      this._entityKey = value;
      this.resetEntity();
    }
  }
  @Input() get customFetcher(): SgEntityFetchAdapter<'_CUSTOM_'> | undefined {
    return this._customFetcher;
  }
  set customFetcher(value: SgEntityFetchAdapter<'_CUSTOM_'>) {
    if (value !== this._customFetcher) {
      this._customFetcher = value;
      this.resetEntity();
    }
  }
  @Input() autoLoadInitial: boolean;
  @Input() appearance: MatFormFieldAppearance;
  @Input() fitContent: boolean;
  @Input() multiple: boolean;
  @Input() overrideArrowIcon: boolean = false;
  @Input() isSlimFormField: boolean;
  @Input() size: 'default' | 'small' = 'default';
  @Input() matchTriggerWidth: boolean = false;
  @Input() subscriptSizing: SubscriptSizing = 'fixed';
  @Input() withFormControl: FormControl;
  @Input() valueTransform: (value: any) => any = (value) => value.value;
  @Input() placeholder: string;
  @Input() get groupBy(): SgEntitySelectAutoCompleteGroupBy | undefined {
    return this._groupBy;
  }
  set groupBy(value: SgEntitySelectAutoCompleteGroupBy | undefined) {
    if (value !== this._groupBy) {
      this._groupBy = value;
      if (typeof value !== 'function') {
        this.wrappedGroupBy = undefined;
      } else {
        this.wrappedGroupBy = Object.assign((obj) => value(obj[OPTIONS_RAW_OBJECT]), { labels: value.labels });
      }
    }
  }

  @Input() beforeSearch: (query: any) => any;
  @Input() afterSearch: <T = any>(query: string, entities: T[]) => T[] | undefined;
  @Input() onSelect?: <T = any>(selection: T | T[]) => void;

  @Output() entitiesFetched = new EventEmitter<any[]>();

  @ViewChild(SgAutoCompleteSelectComponent, { static: true }) autoSelectCmp: SgAutoCompleteSelectComponent;

  options: any[] = [];
  triggerTracker = new AsyncTracker();
  tracker = new AsyncTracker();

  wrappedGroupBy: SgAutoCompleteSelectGroupBy | undefined;

  private _groupBy: SgEntitySelectAutoCompleteGroupBy;
  private _entityKey: keyof SgEntitySelectEntityFetchMap;
  private _customFetcher?: SgEntityFetchAdapter<'_CUSTOM_'>;

  private entity: SgEntityFetchAdapter<keyof SgEntitySelectEntityFetchMap>['metadata'];
  private search$ = new Subject<SgAutoCompleteSelectQueryChange>();

  private _runInitialLoad = true;
  private _lastSearch: { query: string; response: SgEntitySelectSearchResponse<keyof SgEntitySelectEntityFetchMap> };

  constructor(
    private entityStore: SgEntitySelectFetchStore,
    private cdr: ChangeDetectorRef
  ) {
    this.search$
      .pipe(
        distinctUntilChanged(),
        filter((e) => {
          return (!!e.query && e.query.length > 1) || !e.query;
        }),
        debounceTime(250),
        switchMap((event) => {
          return this.ensureEntity().then(() => event);
        }),
        switchMap((event) => {
          return this.doSearch(event.query);
        })
      )
      .subscribe((entities) => {
        this.updateOptions(entities.data);
      });
  }

  ngAfterViewInit(): void {
    const selectionChange = () => {
      if (typeof this.onSelect === 'function') {
        const col = Array.isArray(this.autoSelectCmp.value) ? this.autoSelectCmp.value : [this.autoSelectCmp.value];
        const options = col
          .map((c) => this.options.find((o) => o.value === c))
          .filter((c) => !!c)
          .map((o) => o[OPTIONS_RAW_OBJECT]);
        this.onSelect(this.multiple ? options : options[0]);
      }
    };

    this.autoSelectCmp.selectionChange.pipe(UnRx(this)).subscribe(selectionChange);

    if (this.withFormControl && this.entityKey) {
      const value = this.withFormControl.value;
      if (value) {
        this.updateSelectedValue(value);
        this.entitiesFetched.pipe(take(1)).subscribe(selectionChange);
      } else {
        this.withFormControl.valueChanges.pipe(takeUntil(this.search$), UnRx(this)).subscribe((value) => {
          if (value !== null && value !== undefined) {
            this.updateSelectedValue(value, true);
          }
        });
      }
      this.withFormControl.valueChanges.pipe(UnRx(this)).subscribe((value) => {
        if (value === null || value === undefined) {
          this._runInitialLoad = true;
          this.options = [];
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.search$.complete();
  }

  autoSelectSearchQueryChange(event: SgAutoCompleteSelectQueryChange) {
    this.search$.next(event);
  }

  openedChange(state: boolean): void {
    if (
      this._runInitialLoad &&
      state &&
      this.autoLoadInitial &&
      !this.withFormControl?.value &&
      this.options.length === 0
    ) {
      this._runInitialLoad = false;
      this.ensureEntity()
        .then(() => {
          this.doSearch('')
            .pipe(take(1), UnRx(this))
            .subscribe((results) => {
              this.updateOptions(results.data);
            });
        })
        .catch((error) => {
          console.error('Error in initial load:', error);
        });
    }
  }

  scrollEnd(): void {
    if (
      this._lastSearch?.response.infiniteScrollInfo?.currentPage <
      this._lastSearch?.response.infiniteScrollInfo?.totalPages
    ) {
      const nextPage = this._lastSearch.response.infiniteScrollInfo.currentPage + 1;
      this.doSearch(this._lastSearch.query, nextPage).subscribe((response) => {
        this.updateOptions(response.data, true);
      });
    }
  }

  private resetEntity(): void {
    this.entity = undefined;
    this._runInitialLoad = true;
    this.options = [];
  }

  private fetcher(safeAndSilent = false) {
    try {
      // Return early if no entityKey and no customFetcher
      if (!this.entityKey && !this.customFetcher) {
        if (!safeAndSilent) {
          console.warn('No entityKey or customFetcher provided');
        }
        return undefined;
      }

      const result = this.customFetcher || this.entityStore.getFetcher(this.entityKey);
      if (!result) {
        console.warn('No fetcher found for entityKey:', this.entityKey);
      }
      return result;
    } catch (err) {
      console.error('Error getting fetcher for entityKey:', this.entityKey, err);
      if (!safeAndSilent) {
        throw err;
      }
    }
  }

  private updateSelectedValue(value: any, appendToOptions = false): void {
    UnRx.kill(this, CANCEL_TOKEN);

    // Check if fetcher exists before proceeding
    const fetcher = this.fetcher();
    if (!fetcher) {
      return;
    }

    let getAllEntities: Observable<any[]>;
    if (Array.isArray(value)) {
      // Handle array of IDs by fetching each entity individually
      if (value.length === 0) {
        getAllEntities = of([]);
      } else {
        getAllEntities = forkJoin(value.map((id) => fetcher.getEntity(id))).pipe(
          map((results) => results.filter((r) => r != null))
        ) as Observable<any[]>;
      }
    } else {
      value = [value];
      getAllEntities = forkJoin(value.map((id) => fetcher.getEntity(id))) as Observable<any[]>;
    }

    forkJoin([getAllEntities, this.ensureEntity()])
      .pipe(this.triggerTracker.pipe, this.tracker.pipe, UnRx(this, CANCEL_TOKEN))
      .subscribe(([entities]) =>
        this.updateOptions(Array.isArray(entities) ? entities.filter((e) => !!e) : [], appendToOptions)
      );
  }

  private doSearch(query: string, pageNumber?: number) {
    UnRx.kill(this, CANCEL_TOKEN);

    // Return empty result if no fetcher is available
    const fetcher = this.fetcher();
    if (!fetcher) {
      return of({
        data: [],
        page: 0,
        totalCount: 0,
        infiniteScrollInfo: {
          currentPage: 0,
          totalPages: 0,
        },
      });
    }

    let body: any = {};
    const queryField = this.entity?.queryField;
    if (query && queryField) {
      body[queryField.field] = queryField.isArray ? [query] : query;
    }

    if (typeof this.beforeSearch === 'function') {
      body = this.beforeSearch(body) || body;
    }

    return fetcher.findEntities(body, pageNumber).pipe(
      map((results) => {
        this._lastSearch = { query, response: results };
        if (typeof this.afterSearch === 'function') {
          results.data = this.afterSearch(query, results.data) || results.data;
        }
        return results;
      }),
      this.tracker.pipe,
      take(1),
      UnRx(this, CANCEL_TOKEN)
    );
  }

  private getValue(obj: any, path: string | Array<string>) {
    if (!obj || !path) {
      return null;
    }

    try {
      if (Array.isArray(path)) {
        if (path.find((x) => x.match(findPropertiesRegex))) {
          return parseTemplatedKeysToString(path, obj);
        } else {
          return path
            .map((x) => {
              return x.split('.').reduce((accumulator, currentValue) => {
                return accumulator && accumulator[currentValue] !== undefined ? accumulator[currentValue] : null;
              }, obj);
            })
            .filter((x) => x !== null)
            .join(', ');
        }
      } else {
        return path.split('.').reduce((accumulator, currentValue) => {
          return accumulator && accumulator[currentValue] !== undefined ? accumulator[currentValue] : null;
        }, obj);
      }
    } catch (error) {
      console.warn('Error getting value from path:', path, 'on object:', obj, error);
      return null;
    }
  }

  private updateOptions(entities: any[], appendToOptions = false): void {
    if (!entities || !Array.isArray(entities)) {
      console.warn('Invalid entities provided to updateOptions:', entities);
      this.options = [];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      return;
    }

    if (!this.entity || !this.entity.idField || !this.entity.labelField) {
      console.warn('Entity configuration is missing or incomplete:', this.entity);
      this.options = [];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      return;
    }

    // Create options that work with default Angular Material display
    const options = entities
      .filter((r) => r && this.getValue(r, this.entity.idField as string) !== null)
      .map((r) => {
        const label = this.getValue(r, this.entity.labelField as string);
        const value = this.getValue(r, this.entity.idField as string);

        // Create an option object that Angular Material can display automatically
        return {
          label: label,
          value: value,
          viewValue: label, // This is what Angular Material uses for display
          toString: () => label, // Fallback for string conversion
          [OPTIONS_RAW_OBJECT]: r,
        };
      })
      .filter((opt) => opt.label !== null && opt.value !== null);

    if (appendToOptions) {
      this.options = [...this.options, ...options.filter((opt) => !this.options.find((o) => o.value === opt.value))];
    } else {
      this.options = options;
    }

    this.cdr.markForCheck();
    this.cdr.detectChanges();
    this.entitiesFetched.emit(entities);
  }

  private ensureEntity(forceUpdate = false): Promise<void> {
    let p = Promise.resolve();

    if (this.entity && !forceUpdate) {
      return p;
    }

    // Skip entity validation if entityKey is not set yet
    if (!this.entityKey && !this.customFetcher) {
      return p;
    }

    return p.then(() => {
      try {
        const fetcher = this.fetcher(true);
        this.entity = fetcher ? fetcher.metadata : undefined;

        if (!this.entity) {
          const error = new Error(`Invalid entity, "${this.entityKey}" is not a valid entity.`);
          error.name = 'SgEntitySelectAutoCompleteComponent';
          console.error('Entity configuration error:', error);
          throw error;
        }
      } catch (error) {
        console.error('Error in ensureEntity:', error);
        this.entity = undefined;
        throw error;
      }
    });
  }
}
